import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { 
  Users, 
  TrendingUp, 
  Building2, 
  Mail, 
  Phone, 
  MapPin,
  Calendar,
  Filter,
  RefreshCw,
  Trash2,
  CheckCircle,
  Clock,
  XCircle,
  ChevronDown,
  ArrowLeft,
  Search
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const STATUS_OPTIONS = [
  { value: "new", label: "New", color: "bg-blue-500" },
  { value: "contacted", label: "Contacted", color: "bg-yellow-500" },
  { value: "converted", label: "Converted", color: "bg-green-500" },
  { value: "closed", label: "Closed", color: "bg-gray-500" },
];

const AdminDashboard = () => {
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, [filterType, filterStatus]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch stats
      const statsRes = await axios.get(`${API}/admin/stats`);
      setStats(statsRes.data);

      // Build query params
      const params = new URLSearchParams();
      if (filterType !== "all") params.append("inquiry_type", filterType);
      if (filterStatus !== "all") params.append("status", filterStatus);

      // Fetch inquiries
      const inquiriesRes = await axios.get(`${API}/inquiries?${params.toString()}`);
      setInquiries(inquiriesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const updateInquiryStatus = async (inquiryId, newStatus) => {
    try {
      await axios.patch(`${API}/inquiries/${inquiryId}`, { status: newStatus });
      toast.success("Status updated successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const deleteInquiry = async (inquiryId) => {
    if (!window.confirm("Are you sure you want to delete this inquiry?")) return;
    
    try {
      await axios.delete(`${API}/inquiries/${inquiryId}`);
      toast.success("Inquiry deleted");
      fetchData();
      setShowDetailModal(false);
    } catch (error) {
      toast.error("Failed to delete inquiry");
    }
  };

  const openDetail = (inquiry) => {
    setSelectedInquiry(inquiry);
    setShowDetailModal(true);
  };

  const filteredInquiries = inquiries.filter(inq => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      inq.name?.toLowerCase().includes(search) ||
      inq.email?.toLowerCase().includes(search) ||
      inq.company_name?.toLowerCase().includes(search) ||
      inq.phone?.includes(search)
    );
  });

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
    return (
      <span className={`px-2 py-1 text-xs font-medium text-white rounded ${statusConfig.color}`}>
        {statusConfig.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="bg-[#0A0A0A] border-b border-white/5 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="text-gray-400 hover:text-white transition-colors">
                <ArrowLeft size={20} />
              </Link>
              <div>
                <h1 className="text-xl text-white font-semibold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  <span className="gold-text">Maha</span>asetu Admin
                </h1>
                <p className="text-gray-500 text-sm">Inquiry Management Dashboard</p>
              </div>
            </div>
            <button 
              onClick={fetchData}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
              data-testid="refresh-btn"
            >
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
            <div className="obsidian-card p-4">
              <Users className="w-5 h-5 text-[#D4AF37] mb-2" />
              <p className="text-2xl text-white font-semibold">{stats.total}</p>
              <p className="text-gray-500 text-xs">Total Inquiries</p>
            </div>
            <div className="obsidian-card p-4">
              <Clock className="w-5 h-5 text-blue-500 mb-2" />
              <p className="text-2xl text-white font-semibold">{stats.new}</p>
              <p className="text-gray-500 text-xs">New</p>
            </div>
            <div className="obsidian-card p-4">
              <Phone className="w-5 h-5 text-yellow-500 mb-2" />
              <p className="text-2xl text-white font-semibold">{stats.contacted}</p>
              <p className="text-gray-500 text-xs">Contacted</p>
            </div>
            <div className="obsidian-card p-4">
              <CheckCircle className="w-5 h-5 text-green-500 mb-2" />
              <p className="text-2xl text-white font-semibold">{stats.converted}</p>
              <p className="text-gray-500 text-xs">Converted</p>
            </div>
            <div className="obsidian-card p-4">
              <Building2 className="w-5 h-5 text-purple-500 mb-2" />
              <p className="text-2xl text-white font-semibold">{stats.business_inquiries}</p>
              <p className="text-gray-500 text-xs">Business</p>
            </div>
            <div className="obsidian-card p-4">
              <TrendingUp className="w-5 h-5 text-emerald-500 mb-2" />
              <p className="text-2xl text-white font-semibold">{stats.investor_inquiries}</p>
              <p className="text-gray-500 text-xs">Investor</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600" size={18} />
            <input
              type="text"
              placeholder="Search by name, email, company, phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-[#0A0A0A] border border-white/10 text-white pl-11 pr-4 py-3 focus:border-[#D4AF37] outline-none"
              data-testid="search-input"
            />
          </div>
          
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-full md:w-[180px] bg-[#0A0A0A] border-white/10 text-white" data-testid="filter-type">
              <Filter className="w-4 h-4 mr-2 text-gray-500" />
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-white/10">
              <SelectItem value="all" className="text-white hover:bg-white/5">All Types</SelectItem>
              <SelectItem value="business" className="text-white hover:bg-white/5">Business</SelectItem>
              <SelectItem value="investor" className="text-white hover:bg-white/5">Investor</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full md:w-[180px] bg-[#0A0A0A] border-white/10 text-white" data-testid="filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-white/10">
              <SelectItem value="all" className="text-white hover:bg-white/5">All Status</SelectItem>
              {STATUS_OPTIONS.map(status => (
                <SelectItem key={status.value} value={status.value} className="text-white hover:bg-white/5">
                  {status.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Results count */}
        <p className="text-gray-500 text-sm mb-4">
          Showing {filteredInquiries.length} inquiries
        </p>

        {/* Inquiries Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredInquiries.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left text-gray-500 text-xs uppercase tracking-wider py-3 px-4">Date</th>
                  <th className="text-left text-gray-500 text-xs uppercase tracking-wider py-3 px-4">Type</th>
                  <th className="text-left text-gray-500 text-xs uppercase tracking-wider py-3 px-4">Name</th>
                  <th className="text-left text-gray-500 text-xs uppercase tracking-wider py-3 px-4">Company</th>
                  <th className="text-left text-gray-500 text-xs uppercase tracking-wider py-3 px-4">Contact</th>
                  <th className="text-left text-gray-500 text-xs uppercase tracking-wider py-3 px-4">Status</th>
                  <th className="text-left text-gray-500 text-xs uppercase tracking-wider py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInquiries.map((inquiry) => (
                  <tr 
                    key={inquiry.inquiry_id} 
                    className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
                    onClick={() => openDetail(inquiry)}
                    data-testid={`inquiry-row-${inquiry.inquiry_id}`}
                  >
                    <td className="py-4 px-4">
                      <span className="text-gray-400 text-sm">{formatDate(inquiry.created_at)}</span>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        inquiry.inquiry_type === 'business' ? 'bg-purple-500/20 text-purple-400' : 'bg-emerald-500/20 text-emerald-400'
                      }`}>
                        {inquiry.inquiry_type}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-white">{inquiry.name}</span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-gray-400">{inquiry.company_name || '-'}</span>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex flex-col">
                        <a href={`mailto:${inquiry.email}`} className="text-[#D4AF37] text-sm hover:underline" onClick={e => e.stopPropagation()}>
                          {inquiry.email}
                        </a>
                        <a href={`tel:${inquiry.phone}`} className="text-gray-500 text-sm hover:text-white" onClick={e => e.stopPropagation()}>
                          {inquiry.phone}
                        </a>
                      </div>
                    </td>
                    <td className="py-4 px-4" onClick={e => e.stopPropagation()}>
                      <Select 
                        value={inquiry.status || "new"} 
                        onValueChange={(value) => updateInquiryStatus(inquiry.inquiry_id, value)}
                      >
                        <SelectTrigger className="w-[120px] h-8 bg-transparent border-white/10 text-white text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0A0A0A] border-white/10">
                          {STATUS_OPTIONS.map(status => (
                            <SelectItem key={status.value} value={status.value} className="text-white hover:bg-white/5 text-xs">
                              {status.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-4 px-4" onClick={e => e.stopPropagation()}>
                      <button 
                        onClick={() => deleteInquiry(inquiry.inquiry_id)}
                        className="text-gray-500 hover:text-red-500 transition-colors"
                        data-testid={`delete-${inquiry.inquiry_id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-20">
            <Users className="w-16 h-16 text-gray-700 mx-auto mb-4" />
            <h3 className="text-xl text-white mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              No inquiries found
            </h3>
            <p className="text-gray-500">
              {filterType !== "all" || filterStatus !== "all" || searchTerm
                ? "Try adjusting your filters"
                : "New inquiries will appear here"}
            </p>
          </div>
        )}
      </main>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          {selectedInquiry && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Inquiry <span className="gold-text">Details</span>
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                <div className="flex items-center justify-between">
                  <span className={`px-3 py-1 text-xs font-medium rounded ${
                    selectedInquiry.inquiry_type === 'business' ? 'bg-purple-500/20 text-purple-400' : 'bg-emerald-500/20 text-emerald-400'
                  }`}>
                    {selectedInquiry.inquiry_type?.toUpperCase()}
                  </span>
                  {getStatusBadge(selectedInquiry.status || 'new')}
                </div>

                <div className="obsidian-card p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <Users className="w-4 h-4 text-gray-500" />
                    <div>
                      <p className="text-gray-500 text-xs">Name</p>
                      <p className="text-white">{selectedInquiry.name}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-gray-500" />
                    <div>
                      <p className="text-gray-500 text-xs">Email</p>
                      <a href={`mailto:${selectedInquiry.email}`} className="text-[#D4AF37] hover:underline">
                        {selectedInquiry.email}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-gray-500" />
                    <div>
                      <p className="text-gray-500 text-xs">Phone</p>
                      <a href={`tel:${selectedInquiry.phone}`} className="text-white hover:text-[#D4AF37]">
                        {selectedInquiry.phone}
                      </a>
                    </div>
                  </div>
                  
                  {selectedInquiry.company_name && (
                    <div className="flex items-center gap-3">
                      <Building2 className="w-4 h-4 text-gray-500" />
                      <div>
                        <p className="text-gray-500 text-xs">Company</p>
                        <p className="text-white">{selectedInquiry.company_name}</p>
                      </div>
                    </div>
                  )}
                  
                  {selectedInquiry.location && (
                    <div className="flex items-center gap-3">
                      <MapPin className="w-4 h-4 text-gray-500" />
                      <div>
                        <p className="text-gray-500 text-xs">Location</p>
                        <p className="text-white">{selectedInquiry.location}</p>
                      </div>
                    </div>
                  )}
                  
                  {selectedInquiry.category && (
                    <div className="flex items-center gap-3">
                      <Filter className="w-4 h-4 text-gray-500" />
                      <div>
                        <p className="text-gray-500 text-xs">Category</p>
                        <p className="text-white">{selectedInquiry.category}</p>
                      </div>
                    </div>
                  )}
                  
                  {selectedInquiry.investment_range && (
                    <div className="flex items-center gap-3">
                      <TrendingUp className="w-4 h-4 text-gray-500" />
                      <div>
                        <p className="text-gray-500 text-xs">Investment Range</p>
                        <p className="text-white">{selectedInquiry.investment_range}</p>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-3">
                    <Calendar className="w-4 h-4 text-gray-500" />
                    <div>
                      <p className="text-gray-500 text-xs">Submitted</p>
                      <p className="text-white">{formatDate(selectedInquiry.created_at)}</p>
                    </div>
                  </div>
                </div>

                {selectedInquiry.message && (
                  <div className="obsidian-card p-4">
                    <p className="text-gray-500 text-xs mb-2">Message</p>
                    <p className="text-white whitespace-pre-wrap">{selectedInquiry.message}</p>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <Select 
                    value={selectedInquiry.status || "new"} 
                    onValueChange={(value) => {
                      updateInquiryStatus(selectedInquiry.inquiry_id, value);
                      setSelectedInquiry({...selectedInquiry, status: value});
                    }}
                  >
                    <SelectTrigger className="flex-1 bg-black/50 border-white/10 text-white">
                      <SelectValue placeholder="Update Status" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      {STATUS_OPTIONS.map(status => (
                        <SelectItem key={status.value} value={status.value} className="text-white hover:bg-white/5">
                          {status.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <button 
                    onClick={() => deleteInquiry(selectedInquiry.inquiry_id)}
                    className="px-4 py-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors flex items-center gap-2"
                  >
                    <Trash2 size={16} />
                    Delete
                  </button>
                </div>

                <p className="text-gray-600 text-xs text-center">
                  Inquiry ID: {selectedInquiry.inquiry_id}
                </p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
